import { memo, useMemo } from "react";
import { getSecurityKey } from "../../util/security";
import { useSelector } from "react-redux";
import { selectPositionLtpsSignature } from "../../selectors/security";
import { toLocaleStringIn } from "../../util/general";
import { GroupedPosition } from "../../reducer/positions.reducer";
import { getPositionSecurityKeys, parseLtpsSignature } from "../../util/positionLtpsSignature";

export type LtpCellProps = {
    position: GroupedPosition
}

const LtpCell: React.FC<LtpCellProps> = ({position}) => {

    const { security, quantity } = position;
    const securityKey = getSecurityKey(security.exchange, security.code);
    const isBasket = (position.children?.length ?? 0) > 1;
    const positionSecurityKeys = useMemo(
        () => (isBasket ? getPositionSecurityKeys(position) : [securityKey]),
        [position, securityKey, isBasket]
    );
    const ltpsSignature = useSelector(selectPositionLtpsSignature(positionSecurityKeys));

    const ltpsByKey = useMemo(() => {
        const map = new Map<string, number>();
        const ltps = parseLtpsSignature(ltpsSignature);
        positionSecurityKeys.forEach((key, i) => {
            map.set(key, ltps[i] ?? 0);
        });
        return map;
    }, [ltpsSignature, positionSecurityKeys]);

    const basketLTP = useMemo(() => {
        if (!isBasket || position.quantity === 0) return 0;
        return position.children?.reduce((sum, p) => {
            const key = getSecurityKey(p.security.exchange, p.security.code);
            const ltp = ltpsByKey.get(key) ?? 0;
            return sum + ltp * p.quantity;
        }, 0) || 0;
    }, [position, isBasket, ltpsByKey]);

    const displayLtp = isBasket
        ? Math.abs(basketLTP / quantity)
        : (ltpsByKey.get(securityKey) ?? 0);

    return <span>{ (quantity !== 0 && toLocaleStringIn(displayLtp / (security.priceMultiplier ?? 100))) || "-"}</span>;
}


export default memo(LtpCell);
